<script setup>
import { computed, h, ref } from 'vue';
import {
  useVueTable,
  createColumnHelper,
  getCoreRowModel,
} from '@tanstack/vue-table';
import { useI18n } from 'vue-i18n';

import Spinner from 'shared/components/Spinner.vue';
import EmptyState from 'dashboard/components/widgets/EmptyState.vue';
import Table from 'dashboard/components/table/Table.vue';
import Pagination from 'dashboard/components/table/Pagination.vue';
import AgentCell from './AgentCell.vue';
import DropdownMenu from 'dashboard/components-next/dropdown-menu/DropdownMenu.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import { useToggle } from '@vueuse/core';

const { agents, agentMetrics, pageIndex } = defineProps({
  agents: {
    type: Array,
    default: () => [],
  },
  agentMetrics: {
    type: Array,
    default: () => [],
  },
  isLoading: {
    type: Boolean,
    default: false,
  },
  pageIndex: {
    type: Number,
    default: 1,
  },
});

const emit = defineEmits(['pageChange', 'filteredAgent']);
const { t } = useI18n();

const selectedAgent = ref(null);

function getAgentInformation(id) {
  return agents?.find(agent => agent.id === Number(id));
}
const [showAgentDropdown, toggleAngetDropdown] = useToggle();

const totalCount = ref(agents.length);

const tableData = computed(() => {
  return agentMetrics
    .filter(agentMetric => getAgentInformation(agentMetric.id))
    .map(agent => {
      const agentInformation = getAgentInformation(agent.id);
      if (!agentInformation) {
        return null;
      }
      return {
        agent: agentInformation.name || agentInformation.available_name,
        email: agentInformation.email,
        thumbnail: agentInformation.thumbnail,
        open: agent.metric.open || 0,
        unattended: agent.metric.unattended || 0,
        resolved_today: agent.metric.resolved_today || 0,
        status: agentInformation.availability_status,
      };
    })
    .filter(Boolean);
});

const defaulSpanRender = cellProps =>
  h(
    'span',
    {
      class: cellProps.getValue() ? '' : 'text-slate-300 dark:text-slate-700',
    },
    cellProps.getValue() ? cellProps.getValue() : '---'
  );

const columnHelper = createColumnHelper();
const columns = [
  columnHelper.accessor('agent', {
    header: t('OVERVIEW_REPORTS.AGENT_CONVERSATIONS.TABLE_HEADER.AGENT'),
    cell: cellProps => h(AgentCell, cellProps),

    size: 250,
  }),
  columnHelper.accessor('open', {
    header: t('OVERVIEW_REPORTS.AGENT_CONVERSATIONS.TABLE_HEADER.OPEN'),
    cell: defaulSpanRender,
    size: 100,
  }),
  columnHelper.accessor('unattended', {
    header: () =>
      h(
        'span',
        {
          title: 'Clientes que estão aguardando resposta do agente',
        },
        t('OVERVIEW_REPORTS.AGENT_CONVERSATIONS.TABLE_HEADER.UNATTENDED')
      ),
    cell: defaulSpanRender,
    size: 100,
  }),
  columnHelper.accessor('resolved_today', {
    header: 'Resolvidas hoje',
    cell: defaulSpanRender,
    size: 120,
  }),
];

const paginationParams = computed(() => {
  return {
    pageIndex: pageIndex,
    pageSize: 25,
  };
});

const agentMenuItems = computed(() => {
  return [
    {
      label: 'Todos os agentes',
      value: null,
      action: 'select_agent',
    },
    ...agents.map(agent => ({
      label: agent.name,
      value: agent.id,
      action: 'select_agent',
    })),
  ];
});

const selectedAgentFilter = computed(() => {
  if (!selectedAgent.value) {
    return { label: 'Todos os agentes' };
  }
  return agentMenuItems.value.find(
    item => item.value === selectedAgent.value.id
  );
});

const handleAgentAction = ({ value }) => {
  toggleAngetDropdown(false);
  selectedAgent.value = value ? agents.find(agent => agent.id === value) : null;
  totalCount.value = selectedAgent.value ? 1 : agents.length;
  emit('filteredAgent', selectedAgent?.value?.id);
};

const table = useVueTable({
  get data() {
    return tableData.value;
  },
  columns,
  manualPagination: true,
  enableSorting: false,
  getCoreRowModel: getCoreRowModel(),
  get rowCount() {
    return totalCount.value;
  },
  state: {
    get pagination() {
      return paginationParams.value;
    },
  },
  onPaginationChange: updater => {
    const newPagintaion = updater(paginationParams.value);
    emit('pageChange', newPagintaion.pageIndex);
  },
});
</script>

<template>
  <div class="agent-table-container">
    <div
      v-on-clickaway="() => toggleAngetDropdown(false)"
      class="relative group flex flex-row items-center justify-end mb-4"
    >
      <Button
        sm
        slate
        :label="selectedAgentFilter.label"
        class="rounded-md group-hover:bg-n-alpha-2 max-w-full"
        @click="toggleAngetDropdown()"
      />
      <DropdownMenu
        v-if="showAgentDropdown"
        :menu-items="agentMenuItems"
        show-search
        search-placeholder="Buscar agente"
        class="mt-1 ltr:right-0 rtl:left-0 xl:ltr:right-0 xl:rtl:left-0 top-full !min-w-56 max-w-full max-h-96 overflow-y-auto"
        @action="handleAgentAction($event)"
      />
    </div>
    <Table
      :table="table"
      class="max-h-[calc(100vh-21.875rem)] border border-slate-50 dark:border-slate-800"
    />
    <Pagination class="mt-2" :table="table" />
    <div v-if="isLoading" class="agents-loader">
      <Spinner />
      <span>{{
        $t('OVERVIEW_REPORTS.AGENT_CONVERSATIONS.LOADING_MESSAGE')
      }}</span>
    </div>
    <EmptyState
      v-else-if="!isLoading && !agentMetrics.length"
      :title="$t('OVERVIEW_REPORTS.AGENT_CONVERSATIONS.NO_AGENTS')"
    />
  </div>
</template>

<style lang="scss" scoped>
.agent-table-container {
  @apply flex flex-col flex-1;

  .ve-table {
    &::v-deep {
      th.ve-table-header-th {
        font-size: var(--font-size-mini) !important;
        padding: var(--space-small) var(--space-two) !important;
      }

      td.ve-table-body-td {
        padding: var(--space-one) var(--space-two) !important;
      }
    }
  }

  &::v-deep .ve-pagination {
    @apply bg-transparent dark:bg-transparent;
  }

  &::v-deep .ve-pagination-select {
    @apply hidden;
  }

  .row-user-block {
    @apply items-center flex text-left;

    .user-block {
      @apply items-start flex flex-col min-w-0 my-0 mx-2;

      .title {
        @apply text-sm m-0 leading-[1.2] text-slate-800 dark:text-slate-100;
      }

      .sub-title {
        @apply text-xs text-slate-600 dark:text-slate-200;
      }
    }
  }

  .table-pagination {
    @apply mt-4 text-right;
  }
}

.agents-loader {
  @apply items-center flex text-base justify-center p-8;
}
</style>
